<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add New Penalty</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Create a new penalty record with complete details and
                        tracking information
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-gavel</v-icon>
                      New Penalty Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Penalties
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Basic Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-gavel</v-icon>
                    Penalty Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="users"
                        item-text="name"
                        item-value="id"
                        label="Select User"
                        placeholder="Choose the user to penalize"
                        outlined
                        :error-messages="errors.user_id"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                        <template v-slot:item="{ item, attrs, on }">
                          <v-list-item v-bind="attrs" v-on="on">
                            <v-list-item-avatar size="32">
                              <v-icon>mdi-account</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title>{{
                                item.name
                              }}</v-list-item-title>
                              <v-list-item-subtitle>{{
                                item.email
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.penalty_type_id"
                        :items="penaltyTypes"
                        item-text="name"
                        item-value="id"
                        label="Penalty Type"
                        placeholder="Select penalty type"
                        outlined
                        :error-messages="errors.penalty_type_id"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-format-list-bulleted</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="form.status"
                        :items="statusOptions"
                        label="Status"
                        placeholder="Select penalty status"
                        outlined
                        :error-messages="errors.status"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-flag</v-icon>
                        </template>
                      </v-select>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.issued_by_id"
                        :items="users"
                        item-text="name"
                        item-value="id"
                        label="Issued By"
                        placeholder="Select who issued the penalty"
                        outlined
                        :error-messages="errors.issued_by_id"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account-tie</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.remark"
                        label="Penalty Remarks"
                        placeholder="Enter detailed reason for the penalty"
                        outlined
                        rows="4"
                        :rules="[(v) => !!v || 'Penalty remarks are required']"
                        :error-messages="errors.remark"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-text</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>
                </div>

                <!-- Penalty Type Specific Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-calendar-clock</v-icon
                    >
                    Penalty Details
                  </h3>

                  <!-- Penalty Type Selection -->
                  <v-row>
                    <v-col cols="12">
                      <v-radio-group
                        v-model="penaltyCategory"
                        row
                        class="mt-0"
                        hide-details
                      >
                        <v-radio
                          label="Ban/Suspension"
                          value="ban"
                          color="error"
                        ></v-radio>
                        <v-radio
                          label="Fine/Monetary"
                          value="fine"
                          color="warning"
                        ></v-radio>
                        <v-radio
                          label="Warning Only"
                          value="warning"
                          color="info"
                        ></v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-row>

                  <!-- Ban Details (shown when ban is selected) -->
                  <v-row v-if="penaltyCategory === 'ban'">
                    <v-col cols="12" md="6">
                      <v-menu
                        v-model="banFromMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="form.ban_from"
                            label="Ban Start Date"
                            placeholder="Select start date"
                            outlined
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors.ban_from"
                            class="professional-field"
                            hide-details="auto"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon color="grey">mdi-calendar-start</v-icon>
                            </template>
                          </v-text-field>
                        </template>
                        <v-date-picker
                          v-model="form.ban_from"
                          @input="banFromMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-menu
                        v-model="banToMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="form.ban_to"
                            label="Ban End Date"
                            placeholder="Select end date"
                            outlined
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors.ban_to"
                            class="professional-field"
                            hide-details="auto"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon color="grey">mdi-calendar-end</v-icon>
                            </template>
                          </v-text-field>
                        </template>
                        <v-date-picker
                          v-model="form.ban_to"
                          @input="banToMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>

                  <!-- Fine Details (shown when fine is selected) -->
                  <v-row v-if="penaltyCategory === 'fine'">
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.fine_amount"
                        label="Fine Amount"
                        placeholder="Enter fine amount"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :rules="[
                          (v) =>
                            !v ||
                            parseFloat(v) >= 0 ||
                            'Fine amount must be 0 or greater',
                        ]"
                        :error-messages="errors.fine_amount"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-currency-inr</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Additional Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-information</v-icon
                    >
                    Additional Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-menu
                        v-model="resolvedAtMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="form.resolved_at"
                            label="Resolved At (Optional)"
                            placeholder="Select resolution date"
                            outlined
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors.resolved_at"
                            class="professional-field"
                            hide-details="auto"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon color="grey">mdi-calendar-check</v-icon>
                            </template>
                          </v-text-field>
                        </template>
                        <v-date-picker
                          v-model="form.resolved_at"
                          @input="resolvedAtMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </div>

                <!-- Status Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Penalty Status
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3"
                              >mdi-check-circle</v-icon
                            >
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                Active Status
                              </div>
                              <div class="text-caption grey--text">
                                Set whether this penalty is currently active
                              </div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="success"
                              inset
                              hide-details
                              class="ml-3"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.is_active ? 'success' : 'warning'"
                              :text-color="form.is_active ? 'white' : 'black'"
                            >
                              {{ form.is_active ? "Active" : "Inactive" }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors()
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                      :disabled="isSaving"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="savePenalty"
                      :loading="isSaving"
                      :disabled="isSaving || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Penalty
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PenaltyCreatePage",
  data() {
    return {
      valid: false,
      form: {
        user_id: null,
        penalty_type_id: null,
        ban_from: null,
        ban_to: null,
        fine_amount: "",
        remark: "",
        status: 0,
        issued_by_id: null,
        resolved_at: null,
        is_active: true,
      },
      errors: {},
      isSaving: false,
      users: [],
      penaltyTypes: [],
      penaltyCategory: "warning", // ban, fine, warning
      banFromMenu: false,
      banToMenu: false,
      resolvedAtMenu: false,
      statusOptions: [
        { text: "Pending", value: 0 },
        { text: "In Process", value: 1 },
        { text: "Completed", value: 2 },
        { text: "Rejected", value: 3 },
      ],
    };
  },

  mounted() {
    this.getMasters();
  },

  methods: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    async savePenalty() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSaving = true;
      this.errors = {};

      try {
        // Prepare the data
        const penaltyData = {
          user_id: this.form.user_id,
          penalty_type_id: this.form.penalty_type_id,
          ban_from: this.penaltyCategory === "ban" ? this.form.ban_from : null,
          ban_to: this.penaltyCategory === "ban" ? this.form.ban_to : null,
          fine_amount:
            this.penaltyCategory === "fine" && this.form.fine_amount
              ? parseFloat(this.form.fine_amount)
              : null,
          remark: this.form.remark,
          status: this.form.status,
          issued_by_id: this.form.issued_by_id,
          resolved_at: this.form.resolved_at,
          is_active: this.form.is_active,
        };

        const response = await axios.post("/penalties", penaltyData);

        if (response.data.success) {
          this.$store.dispatch("errors/setError", {
            message: "Penalty created successfully",
            type: "success",
          });
          this.$router.push("/penalties");
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while creating the penalty.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            // Validation errors
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            // Authentication error
            this.$store.dispatch("errors/setError", {
              message: "Authentication required. Please login again.",
            });
            this.$router.push("/login");
          } else {
            // Other errors
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while creating the penalty.",
            });
          }
        } else {
          // Network or other errors
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    async getMasters() {
      try {
        const response = await axios.get("masters?context=penalties");
        const masters = response.data;

        // Users
        if (masters.users) {
          this.users = masters.users;
        }

        // Penalty Types (from ValueList - use description field)
        if (masters.penalty_types) {
          this.penaltyTypes = masters.penalty_types.map(penaltyType => ({
            id: penaltyType.id,
            name: penaltyType.description,
            value: penaltyType.id,
            text: penaltyType.description
          }));
        }

        // Positions (if needed)
        if (masters.positions) {
          this.positions = masters.positions;
        }
      } catch (error) {
        console.log("Error loading masters data:", error);
      }
    },
  },

  watch: {
    penaltyCategory(newValue) {
      // Clear values when changing penalty category
      if (newValue !== "ban") {
        this.form.ban_from = null;
        this.form.ban_to = null;
      }
      if (newValue !== "fine") {
        this.form.fine_amount = "";
      }
    },
    // Clear errors when user starts typing
    "form.user_id"() {
      if (this.errors.user_id) {
        this.$delete(this.errors, "user_id");
      }
    },
    "form.remark"() {
      if (this.errors.remark) {
        this.$delete(this.errors, "remark");
      }
    },
    "form.fine_amount"() {
      if (this.errors.fine_amount) {
        this.$delete(this.errors, "fine_amount");
      }
    },
    "form.ban_from"() {
      if (this.errors.ban_from) {
        this.$delete(this.errors, "ban_from");
      }
    },
    "form.ban_to"() {
      if (this.errors.ban_to) {
        this.$delete(this.errors, "ban_to");
      }
    },
  },
};
</script>

<style scoped>
/* Use same styling as users create page */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .section-title {
    font-size: 0.85rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }

  .form-content {
    padding: 0.5rem !important;
  }

  .section-title {
    font-size: 0.8rem;
  }

  .professional-field >>> .v-input__control {
    min-height: 36px;
  }

  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}
</style>